import React, { FC, ReactNode, useMemo } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { Typography } from 'antd';
import { useTranslate } from '@refinedev/core';
import styles from './InternalLayout.module.scss';

const { Title, Text } = Typography;

type StepInfo = {
    current: number;
    total: number;
    label?: ReactNode;
};

interface InternalLayoutProps {
    breadcrumbs?: ReactNode;
    onBack?: () => void;
    backText?: ReactNode;
    extra?: ReactNode;
    title?: ReactNode;
    subtitle?: ReactNode;
    contentTitle?: ReactNode;
    contentSubtitle?: ReactNode;
    sidebar?: ReactNode;
    step?: StepInfo;
    children: ReactNode;
    className?: string;
    contentClassName?: string;
}

export const InternalLayout: FC<InternalLayoutProps> = ({
    breadcrumbs,
    onBack,
    backText,
    extra,
    title,
    subtitle,
    contentTitle,
    contentSubtitle,
    sidebar,
    step,
    children,
    className,
    contentClassName,
}) => {
    const t = useTranslate();

    const defaultBackText = useMemo(
        () =>
            t(
                'applications.layout.go_home',
                undefined,
                'Повернутися на головну'
            ),
        [t]
    );

    // Reserved stepper logic for future use.
    void step;

    const stepContent = useMemo(() => {
        if (!step) return null;

        const { current, total, label } = step;

        if (label) {
            return label;
        }

        return t('applications.layout.step', {
            current,
            total,
        });
    }, [step, t]);

    const defaultSidebar = (
        <nav
            className={styles.sidebarNav}
            aria-label={t('sidebar.navigation', 'Additional links')}
        >
            <a className={styles.sidebarLink} href="/help">
                Допомога
            </a>
                <a className={styles.sidebarLink} href="/contacts">
                    Контакти
                </a>
                <a className={styles.sidebarLink} href="/privacy">
                    Політика конфіденційності
                </a>
                <a className={styles.sidebarLink} href="/terms">
                    Умови використання
                </a>
        </nav>
    );

    return (
        <div className={classNames(styles.root, className)}>
            <div className={styles.container}>
                <div className={styles.inner}> {/* grid layout */}
                    <div className={classNames(styles.main, contentClassName)}>
                        <div className={styles.mainHeader}>
                            <div className={styles.mainTopRow}>
                                {onBack && (
                                    <button
                                        type="button"
                                        className={styles.backLink}
                                        onClick={onBack}
                                >
                                    <ArrowLeftOutlined />
                                    <span>{backText ?? defaultBackText}</span>
                                </button>
                            )}
                        </div>

                            <div className={styles.headerDivider} />

                            <div className={styles.titleGroup}>
                                {title && (
                                    <Title level={1} className={styles.title}>
                                        {title}
                                    </Title>
                                )}
                                {(stepContent || subtitle) && (
                                    <div className={styles.subtitleGroup}>
                                        {stepContent && (
                                            <Text
                                                key={
                                                    step?.current ??
                                                    'current-step'
                                                }
                                                className={styles.stepText}
                                                aria-live="polite"
                                                role="status"
                                            >
                                                <span
                                                    className={
                                                        styles.stepTextContent
                                                    }
                                                >
                                                    {stepContent}
                                                </span>
                                            </Text>
                                        )}
                                        {subtitle && (
                                            <Text className={styles.subtitle}>
                                                {subtitle}
                                            </Text>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {(contentTitle || contentSubtitle) && (
                            <div className={styles.contentHeader}>
                                {contentTitle && (
                                    <Title level={3} className={styles.contentTitle}>
                                        {contentTitle}
                                    </Title>
                                )}
                                {contentSubtitle && (
                                    <Text className={styles.contentSubtitle}>
                                        {contentSubtitle}
                                    </Text>
                                )}
                            </div>
                        )}

                        {children}
                    </div>

                    <aside className={styles.sidebar}>
                        {sidebar ?? defaultSidebar}
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default InternalLayout;
